import { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';
import {
  ProviderSentimentChip,
  NewsSentimentSummary,
  MassiveInsightReasoning,
  KeywordChipRow,
  ActiveKeywordFilter,
} from '@/components/news/NewsBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WatchlistNews — renders a stacked news feed for every ticker in the
 * user's watchlist, grouped by ticker. Mirrors the inline-news look of
 * the Portfolio view (PortfolioView.NewsInline) so the two pages feel
 * consistent. Only mounted while the user has the News toggle on.
 *
 * Each ticker block fetches up to 3 articles from
 * `/api/stocks/{ticker}/news` (same endpoint the Portfolio view uses),
 * which is already cached server-side, so the cost is just one cheap
 * round-trip per ticker on first toggle.
 */
function TickerNewsBlock({ ticker }) {
  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-ticker keyword filter — clicking a Massive keyword chip on any
  // article narrows THIS block's list. Each block has its own filter
  // (one stock's `#earnings` filter doesn't affect another's view).
  const [selectedKeyword, setSelectedKeyword] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedKeyword(null);
    // Pull 5 articles (was 3). With the backend's interleave order
    // [Massive, Yahoo, Massive, Yahoo, Massive…], top-5 = exactly 3
    // Massive + 2 Yahoo. Finnhub fills in when one provider thins out
    // (backend threshold: combined < 6 fires Finnhub).
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setArticles(res.data?.articles?.slice(0, 5) || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [ticker]);

  const filtered = selectedKeyword
    ? (articles || []).filter((a) => {
        const kws = Array.isArray(a.keywords) ? a.keywords : [];
        return kws.some((k) => (k || '').toLowerCase() === selectedKeyword.toLowerCase());
      })
    : (articles || []);

  return (
    <div className="rounded-lg border bg-card overflow-hidden mb-2" data-testid={`watchlist-news-block-${ticker}`}>
      <div className="px-4 py-2 bg-muted/30 border-b border-border/30 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-bold tracking-wider text-foreground" data-testid={`watchlist-news-ticker-${ticker}`}>{ticker}</span>
        <div className="flex items-center gap-2">
          {!loading && articles && articles.length >= 2 && (
            <NewsSentimentSummary articles={articles} />
          )}
          {!loading && articles && (
            <span className="text-[10px] text-muted-foreground">
              {articles.length === 0 ? 'No news' : `${articles.length} article${articles.length === 1 ? '' : 's'}`}
            </span>
          )}        </div>
      </div>
      {loading ? (
        <div className="px-4 py-3 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !articles || articles.length === 0 ? (
        <div className="px-4 py-2.5 text-xs text-muted-foreground">No recent news for {ticker}.</div>
      ) : (
        <div className="divide-y divide-border/20">
          {selectedKeyword && (
            <div className="px-4 py-2">
              <ActiveKeywordFilter
                keyword={selectedKeyword}
                matchCount={filtered.length}
                totalCount={articles.length}
                onClear={() => setSelectedKeyword(null)}
              />
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="px-4 py-2.5 text-xs text-muted-foreground">
              No articles match #{selectedKeyword} for {ticker}.
            </div>
          ) : filtered.map((article, i) => (
            <div
              key={i}
              className="px-4 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors group"
              onClick={() => {
                const url = article.url || `https://www.google.com/search?q=${encodeURIComponent(article.title)}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              data-testid={`watchlist-news-article-${ticker}-${i}`}
            >
              <div className="flex items-start gap-2">
                {/* Yahoo News thumbnail (when present). Same inline-only
                    sizing as PortfolioView's NewsInline so the row
                    height stays consistent. Sep 26 2026 — use
                    `object-contain` so publisher wordmark thumbnails
                    (Yahoo's purple "f", Benzinga OG images, etc.)
                    render whole instead of cropped to the middle few
                    letters. At 40×40 the letterbox on real photos is
                    a non-issue. */}
                {article.thumbnail && (
                  <img
                    src={article.thumbnail}
                    alt=""
                    loading="lazy"
                    className="w-10 h-10 rounded object-contain shrink-0 bg-muted p-0.5"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid={`watchlist-news-thumb-${ticker}-${i}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2">{article.title}</p>
                  <KeywordChipRow article={article} selected={selectedKeyword} onToggle={setSelectedKeyword} />
                  {article.summary && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{article.summary}</p>
                  )}
                  <MassiveInsightReasoning article={article} compact />
                  <div className="flex items-center gap-2 mt-1 text-[9px] text-muted-foreground flex-wrap">
                    <span>{article.source}</span>
                    <ProviderSentimentChip
                      provider={article.provider}
                      sentiment={article.sentiment}
                      compact
                    />
                    <span>{article.age}</span>
                  </div>
                </div>
                <ExternalLink className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-0.5 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { TickerNewsBlock };

export default function WatchlistNews({ tickers }) {
  if (!tickers || tickers.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4 mb-3 text-xs text-muted-foreground text-center" data-testid="watchlist-news-empty">
        Add stocks to your watchlist to see their news here.
      </div>
    );
  }

  return (
    <div className="mb-3" data-testid="watchlist-news-feed">
      {tickers.map(t => <TickerNewsBlock key={t} ticker={t} />)}
    </div>
  );
}
